import {
  AfterViewInit,
  Directive,
  ElementRef,
  inject,
  NgZone,
  OnDestroy,
} from '@angular/core';

@Directive({
  selector: '[prFitTimelineDate]',
})
export class FitTimelineDateDirective implements AfterViewInit, OnDestroy {
  private readonly elementRef = inject(ElementRef);
  private readonly ngZone = inject(NgZone);
  private resizeObserver?: ResizeObserver;
  private mutationObserver?: MutationObserver;
  private timelineDay?: HTMLElement;

  ngAfterViewInit(): void {
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.timelineDay = this._findTimelineDay();
        if (this.timelineDay) {
          this._adjustScale();
          this._setupObservers();
        }
      }, 0);
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
  }

  private _findTimelineDay(): HTMLElement | undefined {
    const nativeElement = this.elementRef.nativeElement as HTMLElement;
    return nativeElement.parentElement?.closest('.timeline-day') ?? undefined;
  }

  private _setupObservers(): void {
    if (!this.timelineDay) {
      return;
    }

    const element = this.elementRef.nativeElement as HTMLElement;

    this.resizeObserver = new ResizeObserver(() => this._adjustScale());
    this.resizeObserver.observe(this.timelineDay);

    this.mutationObserver = new MutationObserver(() => this._adjustScale());
    this.mutationObserver.observe(element, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  }

  private _adjustScale(): void {
    if (!this.timelineDay) {
      return;
    }

    const element = this.elementRef.nativeElement as HTMLElement;
    const dayLabel = element.parentElement;
    if (!dayLabel) {
      return;
    }

    const dayLabelStyle = getComputedStyle(dayLabel);
    const isHorizontalMode = dayLabelStyle.writingMode === 'vertical-lr';

    const originalWritingMode = dayLabel.style.writingMode;
    if (isHorizontalMode) {
      dayLabel.style.writingMode = 'horizontal-tb';
    }

    const elementWidth = element.scrollWidth;

    if (isHorizontalMode) {
      dayLabel.style.writingMode = originalWritingMode;
    }

    const parentStyle = getComputedStyle(this.timelineDay);
    const margin = 8;

    const availableWidth = isHorizontalMode
      ? this.timelineDay.clientHeight -
        parseFloat(parentStyle.paddingTop) -
        parseFloat(parentStyle.paddingBottom) -
        margin
      : this.timelineDay.clientWidth -
        parseFloat(parentStyle.paddingLeft) -
        parseFloat(parentStyle.paddingRight) -
        margin;

    const needsScaling = elementWidth > availableWidth && availableWidth > 0;
    const scale = needsScaling
      ? Math.max(availableWidth / elementWidth, 0.5)
      : 1;

    const rotation = isHorizontalMode ? 'rotate(180deg) ' : '';
    element.style.transform = `${rotation}scale(${scale})`;
  }
}
